import { AutocompleteWithLabel, InputWithLabel } from '@adotel/components'
import { useLazyQuery, useMutation } from '@apollo/client'
import { Typography, Grid, Switch } from '@material-ui/core'
import SearchIcon from '@material-ui/icons/Search'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'

import SectionTitle from '../../components/SectionTitle'
import {
  MODIFY_CHANNEL_MAPPING_WITHOUT_RATEPLAN_MUTATION,
  GENERATE_CHANNEL_MAPPING_WITHOUT_RATEPLAN_MUTATION,
  MODIFY_CHANNEL_MAPPING_WITH_RATEPLAN_MUTATION,
  GENERATE_CHANNEL_MAPPING_WITH_RATEPLAN_MUTATION,
  ACCOMMODATIONS_FOR_CHANNEL_QUERY,
  CHANNEL_MAPPING_DATA_FOR_ACCOMMODATION_QUERY
} from '../../graphql/channelMapping'
import { SupportDistributionSuggestion } from '../../helpers/supportScreenContext'
import useChannelMappingSupport from '../../helpers/useChannelMappingSupport'
import { getTranslate } from '../../localization'
import {
  AccommodationForChannelMapping,
  AccommodationsForChannel,
  ChannelMappingWithRatePlanInput,
  ChannelMappingWithRatePlanParameterInput,
  ChannelMappingWithoutRatePlanInput,
  ChannelMappingWithoutRatePlanParameterInput
} from '../../types/channelMapping'
import { Footer } from './Footer'
import RatePlansCodeMappingModal from './RatePlansCodeMappingModal'
import RatePlansSelectionModal from './RatePlansSelectionModal'
import RoomTypeItemBox from './RoomTypeItemBox'
import RoomTypesList from './RoomTypesList'
import useStyles from './styles/index.styles'

interface ChannelMappingProps {
  channelCode: string
  onSupportMappingChange?: (
    record: SupportDistributionSuggestion | undefined
  ) => void
}

export const ChannelMapping: React.FC<ChannelMappingProps> = ({
  channelCode,
  onSupportMappingChange
}) => {
  const classes = useStyles()
  const {
    record: supportMapping,
    begin: beginSupportMapping,
    clear: clearSupportMapping
  } = useChannelMappingSupport(channelCode)
  useEffect(() => {
    onSupportMappingChange?.(supportMapping)
    return () => onSupportMappingChange?.(undefined)
  }, [onSupportMappingChange, supportMapping])
  const [haveSuppurtRatePlans, setHaveSuppurtRatePlans] =
    useState<boolean>(true)
  const [searchInput, setSearchInput] = useState<string>('')
  const [parametersForWithoutRateplan, setParametersForWithoutRateplan] =
    useState<ChannelMappingWithoutRatePlanInput>({
      active: false,
      channelCode: '',
      parameters: []
    })

  const [
    updatedParametersForWithoutRateplan,
    setUpdatedParametersForWithoutRateplan
  ] = useState<ChannelMappingWithoutRatePlanInput>({
    active: false,
    channelCode: '',
    parameters: []
  })

  const [parametersForMultiRateplan, setParametersForMultiRateplan] =
    useState<ChannelMappingWithRatePlanInput>({
      active: false,
      channelCode: '',
      parameters: []
    })

  const [
    updatedParametersForMultiRateplan,
    setUpdatedParametersForMultiRateplan
  ] = useState<ChannelMappingWithRatePlanInput>({
    active: false,
    channelCode: '',
    parameters: []
  })

  const [autoCompleteForAccommodation, setAutoCompleteForAccommodation] =
    useState<any>(null)

  const [channelCodeValue, setChannelCodeValue] = useState<string>('')

  const [accommodationChannelCode, setAccommodationChannelCode] =
    useState<string>('')

  const [
    getAccommodationsForChannel,
    {
      data: accommodationsForChannel,
      loading: loadingForAccommodationsForChannel
    }
  ] = useLazyQuery<{
    accommodationsForChannel: AccommodationsForChannel
  }>(ACCOMMODATIONS_FOR_CHANNEL_QUERY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    onCompleted: (res) => {
      setParametersForWithoutRateplan({
        active: res.accommodationsForChannel.Active || false,
        channelCode: '',
        parameters: []
      })
      setUpdatedParametersForWithoutRateplan({
        active: res.accommodationsForChannel.Active || false,
        channelCode: '',
        parameters: []
      })
      setParametersForMultiRateplan({
        active: res.accommodationsForChannel.Active || false,
        channelCode: '',
        parameters: []
      })
      setUpdatedParametersForMultiRateplan({
        active: res.accommodationsForChannel.Active || false,
        channelCode: '',
        parameters: []
      })
    },
    onError: (err) => {
      toast.error(err.message)
    }
  })

  const [
    getChannelMappingDataForAccommodation,
    {
      data: channelMappingDataForAccommodation,
      loading: loadingForChannelMappingDataForAccommodation
    }
  ] = useLazyQuery<{
    channelMappingDataForAccommodation: AccommodationForChannelMapping
  }>(CHANNEL_MAPPING_DATA_FOR_ACCOMMODATION_QUERY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    onCompleted: (resp) => {
      setAccommodationChannelCode(
        resp.channelMappingDataForAccommodation.ChannelCode
      )
    },
    onError: (err) => {
      toast.error(err.message)
    }
  })

  useEffect(() => {
    setParametersForWithoutRateplan((prevState) => ({
      active: prevState.active,
      channelCode: channelCodeValue,
      parameters: prevState.parameters
    }))
    setUpdatedParametersForWithoutRateplan((prevState) => ({
      active: prevState.active,
      channelCode: channelCodeValue,
      parameters: prevState.parameters
    }))
    setParametersForMultiRateplan((prevState) => ({
      active: prevState.active,
      channelCode: channelCodeValue,
      parameters: prevState.parameters
    }))
    setUpdatedParametersForMultiRateplan((prevState) => ({
      active: prevState.active,
      channelCode: channelCodeValue,
      parameters: prevState.parameters
    }))
  }, [channelCodeValue])

  const [
    saveChannelMappingWithoutRateplan,
    { loading: loadingForChannelMappingWithoutRateplan }
  ] = useMutation(
    accommodationChannelCode
      ? MODIFY_CHANNEL_MAPPING_WITHOUT_RATEPLAN_MUTATION
      : GENERATE_CHANNEL_MAPPING_WITHOUT_RATEPLAN_MUTATION,
    {
      notifyOnNetworkStatusChange: true,
      onCompleted: (res) => {
        if (accommodationChannelCode) {
          toast.success(res.modifyChannelMappingWithoutRatePlan.message)
        } else {
          toast.success(res.generateChannelMappingWithoutRatePlan.message)
        }
      },
      onError: (error) => {
        toast.error(error.message)
      }
    }
  )

  const [
    saveChannelMappingWithRateplan,
    { loading: loadingForChannelMappingWithRateplan }
  ] = useMutation(
    accommodationChannelCode
      ? MODIFY_CHANNEL_MAPPING_WITH_RATEPLAN_MUTATION
      : GENERATE_CHANNEL_MAPPING_WITH_RATEPLAN_MUTATION,
    {
      notifyOnNetworkStatusChange: true,
      onCompleted: (res) => {
        if (accommodationChannelCode) {
          toast.success(res.modifyChannelMappingWithRatePlan.message)
        } else {
          toast.success(res.generateChannelMappingWithRatePlan.message)
        }
      },
      onError: (error) => {
        toast.error(error.message)
      }
    }
  )

  useEffect(() => {
    if (!!channelCode) {
      getAccommodationsForChannel({
        variables: {
          channelCode: channelCode
        }
      })
    }
  }, [channelCode])

  const selectedAccommodation =
    channelMappingDataForAccommodation?.channelMappingDataForAccommodation

  useEffect(() => {
    if (selectedAccommodation) {
      setChannelCodeValue(selectedAccommodation.ChannelCode || '')
    }
  }, [
    loadingForChannelMappingDataForAccommodation,
    channelMappingDataForAccommodation,
    selectedAccommodation
  ])

  useEffect(() => {
    if (selectedAccommodation) {
      const tempParameters: ChannelMappingWithoutRatePlanParameterInput[] =
        selectedAccommodation.RoomTypes.map((rt) => ({
          AdotelCode: rt.AdotelCode,
          ChannelCode: rt.ChannelCode,
          RatePlan:
            rt.RatePlans.filter((ele) => ele.ChannelCode === 'NoData')[0] &&
            !!rt.RatePlans.filter((ele) => ele.ChannelCode === 'NoData')[0]
              .AdotelCode
              ? rt.RatePlans.filter((ele) => ele.ChannelCode === 'NoData')[0]
                  .AdotelCode
              : ''
        })) || []

      setParametersForWithoutRateplan((prevState) => ({
        active: prevState.active,
        channelCode: prevState.channelCode,
        parameters: tempParameters
      }))
    }
  }, [selectedAccommodation, haveSuppurtRatePlans])

  useEffect(() => {
    const tempParameters: ChannelMappingWithRatePlanParameterInput[] =
      selectedAccommodation?.RoomTypes.map((rt) => ({
        AdotelCode: rt.AdotelCode,
        ChannelCode: rt.ChannelCode
      })) || []

    setParametersForMultiRateplan((prevState) => ({
      active: prevState.active,
      channelCode: accommodationChannelCode,
      parameters: tempParameters
    }))
  }, [selectedAccommodation, haveSuppurtRatePlans])

  useEffect(() => {
    setUpdatedParametersForWithoutRateplan((prevState) => ({
      active: prevState.active,
      channelCode: prevState.channelCode,
      parameters: []
    }))
    setUpdatedParametersForMultiRateplan((prevState) => ({
      active: prevState.active,
      channelCode: prevState.channelCode,
      parameters: []
    }))
  }, [haveSuppurtRatePlans, autoCompleteForAccommodation])

  useEffect(() => {
    if (
      channelMappingDataForAccommodation &&
      channelMappingDataForAccommodation.channelMappingDataForAccommodation &&
      selectedAccommodation
    ) {
      setHaveSuppurtRatePlans(
        selectedAccommodation.RoomTypes.every((rt) =>
          rt.RatePlans.every(
            (rp) => !!rp.ChannelCode && rp.ChannelCode !== 'NoData'
          )
        )
      )
    }
  }, [loadingForChannelMappingDataForAccommodation, selectedAccommodation])

  const onSave = () => {
    if (haveSuppurtRatePlans) {
      saveChannelMappingWithRateplan({
        variables: {
          channelCode: channelCode,
          propertyCode:
            autoCompleteForAccommodation && autoCompleteForAccommodation.code,
          input: updatedParametersForMultiRateplan
        }
      })
    } else {
      saveChannelMappingWithoutRateplan({
        variables: {
          channelCode: channelCode,
          propertyCode:
            autoCompleteForAccommodation && autoCompleteForAccommodation.code,
          input: updatedParametersForWithoutRateplan
        }
      })
    }
  }

  return (
    <div className={classes.root}>
      <SectionTitle title="Mapping" marginBottom />
      <div className={classes.header}>
        <AutocompleteWithLabel
          value={autoCompleteForAccommodation}
          label={getTranslate('Accommodation')}
          placeholder={getTranslate('Select accommodation')}
          options={
            accommodationsForChannel?.accommodationsForChannel.Accommodations?.map(
              (item) => ({
                value: item.value,
                label: item.label,
                code: item.entityCode,
                id: item.id
              })
            ) || []
          }
          onChange={(_, v: any) => {
            if (v) {
              const completeSupportMapping = beginSupportMapping()
              setAutoCompleteForAccommodation(v)
              getChannelMappingDataForAccommodation({
                variables: {
                  channelCode: channelCode,
                  propertyCode: v.code
                }
              }).then(
                (result) =>
                  completeSupportMapping(
                    result.error
                      ? undefined
                      : result.data?.channelMappingDataForAccommodation
                  ),
                () => completeSupportMapping()
              )
            } else {
              clearSupportMapping()
              setAutoCompleteForAccommodation(null)
            }
          }}
          fullWidth
          inputSize="small"
          loading={loadingForAccommodationsForChannel || !!!channelCode}
          loadingText={getTranslate('Loading') + '...'}
        />
        <div className={classes.switchContainerForMapping}>
          <Typography variant="h4">
            {getTranslate('Is mapping active?')}
          </Typography>
          <Typography component="div" className={classes.switch}>
            <Grid
              component="label"
              container
              alignItems="center"
              justify="flex-start">
              <Grid item>{getTranslate('No')}</Grid>
              <Grid item>
                <Switch
                  name="checkRatePlans"
                  color="primary"
                  onChange={() => {
                    setUpdatedParametersForWithoutRateplan((prevState) => ({
                      ...prevState,
                      active: !prevState.active
                    }))
                    setUpdatedParametersForMultiRateplan((prevState) => ({
                      ...prevState,
                      active: !prevState.active
                    }))
                  }}
                  checked={updatedParametersForWithoutRateplan.active}
                />
              </Grid>
              <Grid item>{getTranslate('Yes')}</Grid>
            </Grid>
          </Typography>
        </div>
      </div>
      {autoCompleteForAccommodation && autoCompleteForAccommodation.code ? (
        <>
          <div className={classes.accommodationDetailsContainer}>
            <RoomTypeItemBox
              data={{
                AdotelName:
                  channelMappingDataForAccommodation
                    ?.channelMappingDataForAccommodation.AdotelName || '',
                AdotelCode:
                  channelMappingDataForAccommodation
                    ?.channelMappingDataForAccommodation.AdotelCode || '',
                ChannelCode: channelCodeValue,
                RatePlans: []
              }}
              disableRatePlan
              onChange={(event: React.FormEvent<HTMLInputElement>) => {
                setChannelCodeValue(event.currentTarget.value)
              }}
              haveSuppurtRatePlans={haveSuppurtRatePlans}
              loading={loadingForChannelMappingDataForAccommodation}
            />
            <div className={classes.switchContainerForRateplan}>
              <Typography variant="h4">
                {getTranslate(
                  "Does this accommodation's PMS Supports rateplans?"
                )}
              </Typography>
              <Typography component="div" className={classes.switch}>
                <Grid
                  component="label"
                  container
                  alignItems="center"
                  justify="flex-start"
                  className={classes.switchForRatePlanToRight}>
                  <Grid item>{getTranslate('No')}</Grid>
                  <Grid item>
                    <Switch
                      name="checkRatePlans"
                      color="primary"
                      onChange={() => {
                        setHaveSuppurtRatePlans(!haveSuppurtRatePlans)
                      }}
                      checked={haveSuppurtRatePlans}
                    />
                  </Grid>
                  <Grid item>{getTranslate('Yes')}</Grid>
                </Grid>
              </Typography>
            </div>
          </div>
          <div>
            <InputWithLabel
              label={getTranslate('Room Type')}
              placeholder={getTranslate('Search in room types names & codes')}
              autoComplete="off"
              inputSize="small"
              startIcon={<SearchIcon />}
              value={searchInput}
              onChange={(event) => setSearchInput(event.currentTarget.value)}
            />
          </div>
          <RoomTypesList
            EditModal={
              haveSuppurtRatePlans
                ? RatePlansCodeMappingModal
                : RatePlansSelectionModal
            }
            selectedAccommodation={selectedAccommodation}
            loading={loadingForChannelMappingDataForAccommodation}
            parametersForWithoutRateplan={parametersForWithoutRateplan}
            updatedParametersForWithoutRateplan={
              updatedParametersForWithoutRateplan
            }
            setUpdatedParametersForWithoutRateplan={
              setUpdatedParametersForWithoutRateplan
            }
            parametersForMultiRateplan={parametersForMultiRateplan}
            setParametersForMultiRateplan={setParametersForMultiRateplan}
            updatedParametersForMultiRateplan={
              updatedParametersForMultiRateplan
            }
            setUpdatedParametersForMultiRateplan={
              setUpdatedParametersForMultiRateplan
            }
            haveSuppurtRatePlans={haveSuppurtRatePlans}
            searchInput={searchInput}
          />
        </>
      ) : (
        <div />
      )}
      <Footer
        disabled={
          !autoCompleteForAccommodation ||
          loadingForChannelMappingWithoutRateplan ||
          loadingForChannelMappingWithRateplan ||
          !channelCodeValue
        }
        onClick={onSave}
        loading={
          loadingForChannelMappingWithoutRateplan ||
          loadingForChannelMappingWithRateplan
        }
      />
    </div>
  )
}
